import { useContext, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import type { DistanceUnit } from 'lib/geo/distanceUnits'
import { getUnit, kmToMiles } from 'lib/geo/distanceUnits'

interface DistanceDisplay {
  displayDistance: number
  unitLabel: 'miles' | 'km'
  distanceUnit: DistanceUnit
}

/**
 * Hook to convert a distance in kilometers to the user's preferred unit based on region.
 * US and GB users see miles, all other regions see kilometers.
 *
 * @param distanceKm - The distance in kilometers
 * @returns Object containing displayDistance, unitLabel, and distanceUnit
 */
function useDistanceDisplay(distanceKm: number): DistanceDisplay {
  const { currentRegionCode } = useContext(GeoContext)

  return useMemo(() => {
    const distanceUnit = getUnit(currentRegionCode)
    const displayDistance = distanceUnit === 'mi' ? kmToMiles(distanceKm) : distanceKm
    const unitLabel = distanceUnit === 'mi' ? 'miles' : 'km'

    return { displayDistance, unitLabel, distanceUnit }
  }, [currentRegionCode, distanceKm])
}

export default useDistanceDisplay
